import axios from 'axios';
import { config } from '../config/environment';

const API_BASE_URL = config.coreApiUrl;
const API_TIMEOUT = 30000;

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

if (config.features.debug) {
  console.log('🔌 API Service initialized with base URL:', API_BASE_URL);
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      const token = localStorage.getItem('authToken');
      
      // Only redirect if we have a token and we're not already on login page
      // This means we thought we were authenticated but the server rejected us
      if (token && currentPath !== '/login' && !currentPath.startsWith('/login')) {
        // Check if the request has a special flag to prevent auto-redirect
        // Components can set this in the request config
        const preventRedirect = error.config?.preventAuthRedirect === true;
        
        if (!preventRedirect) {
          // Clear token and user data
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          
          // Dispatch logout event to notify components
          window.dispatchEvent(new Event('userLogout'));
          
          // Redirect with authFailed flag so login page knows to clear tokens
          window.location.href = '/login?authFailed=true';
        }
        // If preventRedirect is true, let the component handle the error
      }
    }
    return Promise.reject(error);
  }
);

// API endpoints
export const API_ENDPOINTS = {
  // Auth endpoints
  AUTH: {
    LOGIN: '/api/users/login',
    LOGIN_GOOGLE: '/api/users/login/google',
    LOGIN_REQUEST_CODE: '/api/users/login/request-code',
    LOGIN_VERIFY_CODE: '/api/users/login/verify-code',
    REGISTER: '/api/users/register',
    REGISTER_CHILD: '/api/users/children/register',
    VERIFY_RELATIONSHIP: '/api/users/verify-relationship',
    VERIFY_EMAIL: '/api/users/verify-email',
    RESEND_VERIFICATION: '/api/users/resend-verification',
    FORGOT_PASSWORD: '/api/users/forgot-password',
    RESET_PASSWORD: '/api/users/reset-password',
  },

  CONTACT: {
    SUBMIT: '/api/contact',
  },
  
  // User endpoints
  USERS: {
    ME: '/api/users/me',
    ME_PASSWORD: '/api/users/me/password',
    PROFILE: '/api/users/me',
    UPDATE: '/api/users',
    CHILDREN: '/api/users/children',
  },
  
  // Course endpoints
  COURSES: {
    LIST: '/api/courses',
    DETAILS: '/api/courses',
    AVAILABLE: '/api/courses/available',
    SEARCH: '/api/courses/search',
  },
  
  // Course code endpoints
  COURSE_CODES: {
    GENERATE: '/api/course-codes/generate',
    REGISTER: '/api/course-codes/register',
    VALIDATE: '/api/course-codes/validate',
    USER_CODES: '/api/course-codes/user',
  },
  
  // Enrollment endpoints
  ENROLLMENTS: {
    USER_ENROLLMENTS: '/api/courses/user',
    ENROLL: '/api/courses/enroll',
    UPDATE_PROGRESS: '/api/courses/enrollments',
  },
  
  // Parent Dashboard endpoints
  PARENT_DASHBOARD: {
    DASHBOARD: '/api/parent-dashboard/current',
    DASHBOARD_BY_ID: '/api/parent-dashboard',
  },
  
  // Child Management endpoints
  CHILD_MANAGEMENT: {
    SEARCH: '/api/children/search',
    LINK: '/api/children/link',
    LINK_VERIFY: '/api/children/link/verify',
    LINK_RESEND: '/api/children/link/resend',
    LINK_PENDING: '/api/children/link/pending',
    REGISTER: '/api/children/register',
    LINKED_CHILDREN: '/api/children/linked',
  },
  
  // Parent Course endpoints
  PARENT_COURSES: {
    SEARCH: '/api/parent/courses/search',
    ENROLL_CHILD: '/api/parent/courses/enroll-child',
    CHILD_ENROLLED_IDS: '/api/parent/courses/child',
    AVAILABLE: '/api/parent/courses/child/{childId}/available',
    ENROLL_BATCH: '/api/parent/courses/child/{childId}/enroll-batch',
    UPDATE_SCHOOL_PROFILE: '/api/parent/child/{childId}/school-profile',
  },

  // Notification endpoints
  NOTIFICATIONS: {
    BASE: '/api/notifications',
    SUMMARY: '/api/notifications/current/summary',
    MARK_ALL_READ: '/api/notifications/current/read-all',
    CLEAR_ALL: '/api/notifications/current/clear-all',
  },
  
  // Curriculum endpoints
  CURRICULUM: {
    COURSE_GROUPS: '/api/curriculum/courses',
    GROUP_TOPICS: '/api/curriculum/courses',
    TOPIC_DETAILS: '/api/curriculum/topics',
    FULL_HIERARCHY: '/api/curriculum/subjects',
  },
  
  // Student Dashboard endpoints
  STUDENT_DASHBOARD: {
    DASHBOARD: '/api/student-dashboard',
    /** Aggregated single-call dashboard for the student homepage. */
    AGGREGATED_DASHBOARD: '/api/student/dashboard',
  },
  
  // Quiz endpoints
  QUIZZES: {
    COMPLETED_TOPICS: '/api/quizzes/completed-topics',
    START_ATTEMPT: '/api/quizzes',
    SUBMIT_ATTEMPT: '/api/quizzes/attempts',
  },
  
  // Ranking endpoints
  RANKING: {
    REQUIZ_MASTER: '/api/ranking/requiz-master',
  },
  
  // Location endpoints
  LOCATIONS: {
    COUNTRIES: '/api/locations/countries',
    COUNCILS: '/api/locations/councils',
    SCHOOLS: '/api/locations/schools',
    CLASSES: '/api/locations/classes',
  },
} as const;

export default api;
